import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ViewToggle } from './components/ViewToggle';
import { PreviewBanner } from './components/PreviewBanner';
import { Toast } from './components/Toast';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    previewAsClient,
    activeProject
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  const onStaffLogin = path.startsWith(STAFF_PATH);

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin && !previewAsClient || onStaffLogin;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed && !previewAsClient) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, previewAsClient, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-0" data-mp="nv0ypuwzss67" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-1" data-mp="ssspumptca9c" />
          <Toast data-id="element-2" data-mp="swgmvvugvest" />
        </>;
    }
    if (previewAsClient) {
      return <>
          <PreviewBanner data-id="element-3" data-mp="38b7cycbvyf1" />
          <ClientApp data-id="element-4" data-mp="3udaw269tuj3n" />
          <Toast data-id="element-5" data-mp="3ubri4mbn8k51" />
        </>;
    }
    return <>
        <PortalApp data-id="element-6" data-mp="d4e4fpsbp6v4" />
        <ViewToggle data-id="element-7" data-mp="d4xpvgh6mddw" />
        <Toast data-id="element-8" data-mp="d3crffrqyaar" />
      </>;
  }
  if (onStaffLogin) {
    /* Nothing renders until the persisted session has been checked, so an
       already-signed-in staff member never sees the login form flash. */
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-9" data-mp="3gj1t090tiz3p" />;
    return <>
        <StaffLogin data-id="element-10" data-mp="3bnurn7v0u6hy" />
        <ViewToggle data-id="element-11" data-mp="3bkgqu420a5cm" />
        <Toast data-id="element-12" data-mp="3qaxt1pmgszak" />
      </>;
  }
  if (!authed) {
    return <>
        <Login data-id="element-13" data-mp="3qd0sf09ic4ry" />
        <ViewToggle data-id="element-14" data-mp="3lkr5zu7lpmuf" />
      </>;
  }
  return <>
      <ClientApp data-id="element-15" data-mp="3lj6b23hdk1w1" />
      <ViewToggle data-id="element-16" data-mp="3limryjfzn7gf" />
      <Toast data-id="element-17" data-mp="2xun35p28lr4h" />
    </>;
}
export function App() {
  return <PortalProvider data-id="element-18" data-mp="2xt29s2rnse87">
      <Portal data-id="element-19" data-mp="2xsk5qjyr8vpq" />
    </PortalProvider>;
}